import { LitElement, css, html } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";

import { subscribeToNewsletter } from "../../data/user";

@customElement("bp-global-footer-email-form")
export class GlobalFooterEmailForm extends LitElement {
  static styles = css`
    :host {
      --global-footer-email-form-height: var(--size-5);
      display: block;
    }

    form {
      display: grid;
      grid-template-rows: auto;
      gap: var(--size-1);
    }

    form > * :focus-visible {
      outline-color: var(--color-blue-20);
    }

    .form-label {
      font-weight: var(--font-weight-medium);
      color: var(--color-neutral-40);
    }

    .form-control {
      display: flex;
      flex-wrap: wrap;
      gap: var(--size-1-5);
    }

    input {
      background-color: var(--color-neutral-60);
      padding-inline-start: var(--size-1-5);
      height: var(--global-footer-email-form-height);
      color: currentColor;
      border: 1px solid var(--color-neutral-45);
      line-height: var(--line-height-snug);
      border-radius: var(--radius-md);
      font-size: var(--font-size-md);
      font-family: var(--font-sans);
      box-sizing: border-box;
      flex: 1 1 75%;
    }

    input:disabled {
      opacity: 0.4;
    }

    input:focus {
      background-color: transparent;
      outline: 3px solid var(--color-blue-30);
      outline-offset: 1px;
      border-color: transparent;
    }

    button {
      appearance: none;
      cursor: pointer;
      box-sizing: border-box;
      flex: 1 1 5%;
      align-items: center;
      justify-content: center;
      vertical-align: middle;
      border: none;
      border-radius: var(--radius-md);
      font-size: var(--button-font-size);
      font-weight: var(--font-weight-medium);
      font-family: var(--font-sans);
      line-height: var(--line-height-none);
      -webkit-user-select: none;
      user-select: none;
      white-space: nowrap;
      height: var(--global-footer-email-form-height);
      padding-inline: var(--size-1-5);
      background-color: var(--color-neutral-50);
      color: var(--color-neutral-00);
      transition: background-color var(--duration-short) var(--ease-in-out);
    }

    button:focus-visible {
      outline: 3px solid var(--color-blue-30);
      outline-offset: 1px;
    }

    button:active {
      background-color: var(--color-neutral-60);
    }

    .success-message,
    .error-message {
      box-sizing: border-box;
      min-height: var(--global-footer-email-form-height);
      display: flex;
      align-items: center;
      padding: var(--size-1-5);
      border-radius: var(--radius-md);
      color: var(--color-neutral-80);
    }

    .success-message {
      background-color: var(--color-green-30);
    }

    .error-message {
      background-color: var(--color-red-30);
    }

    @media (min-width: 768px) {
      .form-control {
        flex-direction: row;
        grid-template-columns: 1fr auto;
      }

      input {
        margin-block-end: 0;
        margin-inline-end: var(--size-1-5);
      }
    }

    @media (hover: hover) {
      button:hover {
        background-color: color-mix(
          in srgb,
          var(--color-neutral-50) 50%,
          var(--color-neutral-60) 50%
        );
      }
    }
  `;

  @property()
  lang: string = "en";

  @property()
  placeholder?: string;

  @property({ attribute: "label-submit" })
  labelSubmit?: string;

  @property({ attribute: "label-success" })
  labelSuccess?: string;

  @property({ attribute: "label-error" })
  labelError?: string;

  @state()
  busy = false;

  @state()
  requestState: "NOT_STARTED" | "SUCCESS" | "ERROR" = "NOT_STARTED";

  @query("input")
  input?: HTMLInputElement;

  @query(".success-message, .error-message")
  private _messageElement?: HTMLDivElement;

  protected updated(changedProperties: Map<PropertyKey, unknown>): void {
    if (
      changedProperties.has("requestState") &&
      this.requestState !== "NOT_STARTED"
    ) {
      this._messageElement?.focus();
    }
  }

  handleSubmit = (e: SubmitEvent) => {
    e.preventDefault();

    if (!this.input) return;

    this.busy = true;

    const types = ["Newsletter"];

    subscribeToNewsletter(this.input.value, types, this.lang)
      .then((success) => {
        if (!success) {
          throw Error("Newsletter subscription failed");
        }
        this.requestState = "SUCCESS";
        document.dispatchEvent(
          new CustomEvent("bp:newsletter_subscribe", {
            detail: { types, trigger: "global.footer" },
          }),
        );
      })
      .catch((error) => {
        this.requestState = "ERROR";
        document.dispatchEvent(
          new CustomEvent("bp:error", {
            detail: { error },
          }),
        );
      })
      .finally(() => {
        this.busy = false;
      });
  };

  render() {
    return html`
      <form aria-busy="${this.busy}" @submit="${this.handleSubmit}">
        <label class="form-label" for="email">
          <slot></slot>
        </label>

        <div aria-live="polite" role="status">
          ${this.requestState === "ERROR"
            ? html`
                <div class="error-message" tabindex="-1">
                  ${this.labelError}
                </div>
              `
            : this.requestState === "SUCCESS"
              ? html`
                  <div class="success-message" tabindex="-1">
                    ${this.labelSuccess}
                  </div>
                `
              : ""}
        </div>

        ${this.requestState === "NOT_STARTED"
          ? html`
              <div class="form-control">
                <input
                  name="email"
                  autocomplete="email"
                  type="email"
                  id="email"
                  placeholder="${this.placeholder}"
                  required
                  ?disabled=${this.busy}
                />

                <button aria-label="Submit" part="button" type="submit">
                  ${this.busy
                    ? html`<bp-spinner size="md"></bp-spinner>`
                    : this.labelSubmit}
                </button>
              </div>
            `
          : ""}
      </form>
    `;
  }
}
